// ============================================
// SITE / TRACKING CONFIG
// ============================================

// Real, canonical site origin — used for <link rel="canonical"> tags.
export const SITE_URL = "https://whatnextedu.com";

// Prefix used ONLY for analytics page_path reporting (GA4 / GTM), so every
// course page view is reported individually, e.g. "/what-next/courses/python"
// instead of every course page being grouped under one generic path.
// This does NOT change the real page URL or the canonical tag — those stay
// the clean address, e.g. https://whatnextedu.com/courses/python
export const TRACKING_PATH_PREFIX = "/what-next";

// ============================================
// CONTACT DETAILS — EDIT THESE WITH YOUR REAL INFO
// ============================================

// WhatsApp number in international format, no "+", no spaces
// e.g. India number +91 9363660077 -> "919363660077"
export const WHATSAPP_NUMBER = "919363660077";

// Phone number shown to users (formatted, human-readable)
export const PHONE_DISPLAY = "+91 9363660077";

// Phone number used in tel: links (digits + country code, no spaces)
export const PHONE_TEL = "+919363660077";

// Contact email
export const EMAIL = "whatnextedudesk@gmail.com";

// ============================================
// WHATSAPP LINK BUILDER
// ============================================
// Usage: buildWhatsAppLink("Hi What Next! I want to know more.")
// If no message is passed, opens WhatsApp chat with no pre-filled text.
export function buildWhatsAppLink(message = "") {
  const base = `https://wa.me/${WHATSAPP_NUMBER}`;
  if (!message) return base;
  return `${base}?text=${encodeURIComponent(message)}`;
}

// ============================================
// COURSES DATA
// ============================================
export const COURSES = [
  {
    id: "python",
    code: "PY",
    name: "Python",
    tagline:
      "Learn Python programming from fundamentals to automation, APIs, data handling, and backend development through live practical sessions.",
    level: "Beginner Friendly",
    detail: {
      intro: [
        "Learning Python isn't about remembering syntax. It's about learning how to think like a developer and solve real problems using code.",
        "Our Python Course Online is designed for people who want more than recorded lessons. Every class happens live, giving you the opportunity to write code alongside a mentor, ask questions as they arise, receive feedback instantly, and gradually build confidence through practice.",
        "Whether you're completely new to programming or looking to add Python to your existing skill set, the course follows a structured learning path that helps you understand concepts instead of simply copying code.",
      ],
      sections: [
        {
          heading: "Learn Python by Building, Not Watching",
          paragraphs: [
            "Programming is a practical skill. The more you write code, the more naturally programming starts to make sense.",
            "Instead of spending hours watching videos without interaction, you'll work through coding exercises, solve practical challenges, and build applications step by step with mentor guidance. Every topic is explained through examples that reflect how Python is used in real software development.",
            "By the end of the course, you won't just know Python syntax—you'll understand how to use it to create working solutions.",
          ],
        },
        {
          heading: "What You'll Learn",
          paragraphs: [
            "The curriculum is designed to build strong programming fundamentals before moving into practical development. You'll learn how to:",
          ],
          bullets: [
            "Write clean and structured Python programs",
            "Work with variables, operators, loops, conditions and functions",
            "Organise data using lists, tuples, dictionaries and sets",
            "Apply object-oriented programming concepts",
            "Read from and write to files",
            "Handle exceptions and debug errors",
            "Work with APIs and external libraries",
            "Connect Python with databases",
            "Automate repetitive tasks using Python",
            "Build complete applications through guided projects",
          ],
          closing:
            "Every module builds on the previous one, making it easier for beginners to learn without feeling overwhelmed.",
        },
        {
          heading: "Why Learn Python?",
          paragraphs: [
            "Python is used because it helps developers build software efficiently across different domains. The same language can be applied to web development, automation, data analysis, artificial intelligence, scripting, testing, and backend systems. Learning Python creates a foundation that can support many different career paths as your interests evolve.",
            "Rather than limiting yourself to a single technology, Python gives you a skill that can grow with your career.",
          ],
        },
        {
          heading: "Learn With Live Mentor Support",
          paragraphs: [
            "One of the biggest challenges of learning online is getting stuck with no one to ask.",
            "That's why every session is conducted live. When something doesn't make sense, you can ask questions immediately, discuss different approaches, and receive guidance before moving on. This creates a learning experience that's much closer to a classroom while giving you the flexibility of studying from anywhere.",
          ],
        },
        {
          heading: "Is This Course Right for You?",
          paragraphs: ["This course is suitable if you are:"],
          bullets: [
            "Starting programming for the first time",
            "A student preparing for a technology career",
            "A graduate looking to improve employability",
            "A working professional planning to upskill",
            "Interested in automation, software development, data, or AI",
          ],
          closing: "No previous programming experience is required.",
        },
        {
          heading: "Build Skills That Stay With You",
          paragraphs: [
            "The objective isn't simply to finish a course.",
            "It's to develop the confidence to read code, write your own programs, solve problems independently, and continue learning as technology evolves.",
            "By combining live instruction, practical coding sessions, mentor guidance, and project-based learning, you'll build a foundation that prepares you for more advanced technologies in the future.",
          ],
        },
      ],
      seo: {
        title:
          "Python Course Online | Live Classes & Practical Projects | What Next",
        metaDescription:
          "Learn Python online through live mentor-led classes, coding practice and practical projects. Build strong Python programming skills with What Next.",
      },
    },
  },

  {
    id: "java",
    code: "JV",
    name: "Java",
    tagline:
      "Master Java programming, object-oriented concepts, Spring Boot fundamentals, and enterprise application development.",
    level: "Beginner Friendly",
    detail: {
      intro: [
        "Java becomes much easier to learn when you understand how the pieces fit together instead of trying to memorise syntax and programming rules.",
        "Our Java Course Online is designed for learners who want to build a strong foundation in Java programming through live, mentor-led classes. You learn concepts during the session, write and test code yourself, ask questions as they come up, and work through programming problems with guidance from your mentor.",
        "Whether you're starting programming from scratch or want to strengthen your existing development skills, the course takes you from Java fundamentals to practical application development through a structured learning path.",
      ],
      sections: [
        {
          heading: "Learn Java by Writing Real Code",
          paragraphs: [
            "Reading Java code is different from being able to write it yourself.",
            "That's why the learning process focuses on practice throughout the course. You'll work through programming exercises, understand how Java handles different problems, and gradually move from simple programs to more structured applications.",
            "Rather than simply following a prepared solution, you'll learn how to break a problem down, choose an appropriate approach, write the code, identify errors, and improve your solution.",
            "The aim is to help you become comfortable writing Java independently—not just completing lessons.",
          ],
        },
        {
          heading: "What You'll Learn",
          paragraphs: [
            "The Java Programming Course starts with the fundamentals and progressively introduces the concepts used to develop structured applications. You'll learn how to:",
          ],
          bullets: [
            "Understand Java syntax, variables, data types and operators",
            "Work with conditions, loops and arrays",
            "Create and use methods",
            "Understand classes and objects",
            "Apply object-oriented programming principles",
            "Work with inheritance, abstraction, encapsulation and polymorphism",
            "Handle exceptions and common programming errors",
            "Work with collections and generics",
            "Understand packages and reusable Java components",
            "Work with databases and application data",
            "Build applications using Java development frameworks",
            "Apply your knowledge through practical projects",
          ],
          closing:
            "Each topic builds on the previous one, helping beginners develop their programming fundamentals before moving into more advanced Java development.",
        },
        {
          heading: "Why Learn Java?",
          paragraphs: [
            "Java is used to build software that needs structured, maintainable and scalable code. It is widely associated with backend systems, enterprise applications, web development and a range of software products.",
            "Learning Java also introduces you to important programming concepts such as object-oriented design, exception handling, collections and application architecture. These concepts can become useful when you move into other programming languages and development frameworks.",
            "For learners planning a career in software development, learning Java online can therefore be a practical way to develop both programming fundamentals and application development skills.",
          ],
        },
        {
          heading: "Learn With Live Mentor Support",
          paragraphs: [
            "Learning to program often means getting stuck on a small error and not knowing what went wrong.",
            "With live Java classes, you don't have to wait until the next day to find an answer. You can ask questions during the session, discuss your approach with the mentor, understand why an error occurred, and continue practising with a clearer understanding.",
            "Classes are conducted live through Google Meet, allowing you to learn remotely while still having direct interaction with your mentor.",
          ],
        },
        {
          heading: "Is This Course Right for You?",
          paragraphs: ["This Java Course Online is suitable if you are:"],
          bullets: [
            "A beginner starting your programming journey",
            "A student preparing for software development roles",
            "A graduate looking to build practical programming skills",
            "A working professional planning to strengthen Java knowledge",
            "A learner interested in backend or application development",
            "Someone who wants to understand object-oriented programming properly",
          ],
          closing:
            "You don't need to be an experienced programmer to start. The course begins with the fundamentals and gradually progresses towards practical development.",
        },
        {
          heading: "Build Skills That Stay With You",
          paragraphs: [
            "Completing a Java course shouldn't be the final objective.",
            "The more valuable outcome is being able to look at a programming problem, understand what needs to be solved, design an approach, write the code, test it and improve it when something doesn't work.",
            "That's what the learning process at What Next is designed to encourage.",
            "Through live Java training, coding practice, mentor guidance and project-based learning, you'll develop a foundation that can support your next step into software development and more advanced Java technologies.",
          ],
        },
      ],
      seo: {
        title:
          "Java Course Online | Live Classes & Practical Training | What Next",
        metaDescription:
          "Learn Java online through live mentor-led classes covering OOP, collections and application development. Build job-ready Java skills with What Next.",
      },
    },
  },
  {
    id: "fullstack",
    code: "FS",
    name: "Full Stack Development",
    tagline:
      "Build responsive websites and web applications using HTML, CSS, JavaScript, React, Node.js, Express, MongoDB, and deployment tools.",
    level: "Intermediate",
    detail: {
      intro: [
        "Building a website is only one part of becoming a full stack developer. The real skill is understanding what happens behind the interface—how a page communicates with a server, how data is stored, how users interact with an application, and how the complete system works together.",
        "Our Full Stack Development Course Online is built around that complete picture. Through live, mentor-led classes, you'll work with front-end and back-end technologies, write code during sessions, solve development problems, and gradually turn individual concepts into working web applications.",
        "Whether you're starting your development journey or looking to move from basic web development into full stack development, the course gives you a structured path from fundamentals to building complete applications.",
      ],
      sections: [
        {
          heading: "Learn Full Stack Development by Building",
          paragraphs: [
            "You can learn HTML, CSS, JavaScript and frameworks separately—but that doesn't necessarily teach you how to build a complete application.",
            "That's why the learning approach focuses on connecting the different parts of development.",
            "You'll start by creating responsive interfaces, then move into JavaScript-based functionality, front-end frameworks, server-side development, databases and application deployment. As you progress, you'll see how these technologies communicate with each other inside a real web application.",
            "Instead of simply following tutorials, you'll practise building features, fixing errors, connecting components and understanding how the pieces work together.",
            'The objective is to help you move from "I know the technology" to "I can build with it."',
          ],
        },
        {
          heading: "What You'll Learn",
          paragraphs: [
            "The curriculum covers the major layers involved in modern web application development. You'll learn how to:",
          ],
          bullets: [
            "Build web pages using HTML5 and CSS3",
            "Create responsive layouts for different screen sizes",
            "Use JavaScript to add functionality and interactivity",
            "Work with modern JavaScript concepts",
            "Build user interfaces using React",
            "Create reusable front-end components",
            "Develop server-side applications with Node.js",
            "Build APIs using Express.js",
            "Work with databases such as MongoDB and MySQL",
            "Connect front-end applications with back-end services",
            "Handle application data and user requests",
            "Use Git and GitHub for version control",
            "Understand application deployment and development workflows",
            "Build complete projects by bringing the front end, back end and database together",
          ],
          closing:
            "The learning path moves progressively from web fundamentals to complete application development, so you understand how each technology fits into the larger system.",
        },
        {
          heading: "Build Complete Web Applications",
          paragraphs: [
            "Full stack development becomes clearer when you stop treating the front end and back end as separate subjects.",
            "During the course, you'll work on practical projects that bring multiple technologies together. You'll create interfaces, connect them to APIs, work with databases, process user requests and build features that behave like parts of a real application.",
            "This project-based approach gives you opportunities to practise the development cycle instead of only completing isolated coding exercises.",
            "By the end, you'll have practical work that demonstrates how you've applied your development skills.",
          ],
        },
        {
          heading: "Why Learn Full Stack Development?",
          paragraphs: [
            "Modern web applications depend on multiple technologies working together. A full stack developer needs to understand more than what appears on the screen.",
            "Learning full stack development gives you exposure to:",
          ],
          bullets: [
            "Front-end development",
            "Back-end development",
            "Databases",
            "APIs",
            "Version control",
            "Application deployment",
            "Web application architecture",
          ],
          closing:
            "This broader understanding can be useful if you want to work as a Full Stack Developer, Web Developer, Front-End Developer or Back-End Developer, or if you eventually want to specialise in one part of the development stack.",
        },
        {
          heading: "Learn With Live Mentor Support",
          paragraphs: [
            "One of the difficult parts of learning web development is knowing what to do when your application doesn't work as expected.",
            "A missing bracket, incorrect API request, database connection issue or unexpected browser error can stop your progress.",
            "With live online full stack development classes, you can raise those problems during the session, explain what you've tried, and work through the issue with your mentor.",
            "Classes are conducted live through Google Meet, so you can learn from wherever you are while still getting direct interaction and guidance throughout the programme.",
          ],
        },
        {
          heading: "Is This Course Right for You?",
          paragraphs: [
            "This Full Stack Development Course Online is suitable if you are:",
          ],
          bullets: [
            "A beginner interested in web development",
            "A student looking to build software development skills",
            "A graduate preparing for a career in IT",
            "Someone with basic HTML, CSS or JavaScript knowledge who wants to progress further",
            "A working professional looking to move into web development",
            "Interested in learning both front-end and back-end development",
            "Looking to build portfolio-ready web applications",
          ],
          closing:
            "If you're new to development, the learning path starts with the fundamentals before moving into more advanced technologies.",
        },
        {
          heading: "Build Skills That Stay With You",
          paragraphs: [
            "Knowing how to use a framework is only one part of being a developer.",
            "The more important skill is being able to take an idea, break it into smaller requirements, decide how the application should work, write the code, connect the different components, test the result and troubleshoot problems along the way.",
            "That's the approach behind the Full Stack Development Training at What Next.",
            "Through live instruction, coding practice, practical projects and mentor guidance, you'll develop the ability to work across the different layers of a web application and understand how they come together to create a functioning product.",
          ],
        },
      ],
      seo: {
        title:
          "Full Stack Development Course Online | Live Training | What Next",
        metaDescription:
          "Learn full stack development online through live mentor-led classes covering HTML, CSS, JavaScript, React, Node.js and MongoDB with What Next.",
      },
    },
  },
  {
    id: "digital-marketing",
    code: "DM",
    name: "Digital Marketing",
    tagline:
      "Learn SEO, Google Ads, Meta Ads, content marketing, analytics, email marketing, and social media strategy.",
    level: "Beginner Friendly",
    detail: {
      intro: [
        "Digital marketing is not just about posting content on social media or running advertisements. It is about understanding how people discover a business, what influences them to take action, and how different digital channels work together to bring measurable results.",
        "Our Digital Marketing Course Online is designed around that complete process. Through live, mentor-led classes, you'll learn how to plan campaigns, optimise websites for search, create content, work with advertising platforms, analyse performance and understand what actually drives enquiries and conversions.",
        "Instead of learning digital marketing as a collection of disconnected tools, you'll work through the different channels and understand how they fit into a practical marketing strategy.",
      ],
      sections: [
        {
          heading: "Learn Digital Marketing by Working on Real Scenarios",
          paragraphs: [
            "Knowing what SEO, Google Ads or social media marketing means is different from knowing how to use them for an actual business.",
            "That's why the learning approach focuses on practical situations.",
            "You'll work through examples of how a business can identify its audience, choose the right digital channels, research keywords, create content, plan campaigns, track results and make improvements based on data.",
            "You'll also get opportunities to work with marketing tools and platforms so that you're not learning concepts in isolation.",
            'The objective is to help you move from "I know what digital marketing is" to "I know how to plan and execute a digital marketing campaign."',
          ],
        },
        {
          heading: "What You'll Learn",
          paragraphs: [
            "The curriculum covers the major areas involved in modern digital marketing. You'll learn how to:",
          ],
          bullets: [
            "Understand digital marketing fundamentals and customer journeys",
            "Conduct keyword and search intent research",
            "Optimise webpages using on-page SEO techniques",
            "Understand technical and off-page SEO fundamentals",
            "Create SEO-focused content",
            "Plan and manage Google Ads campaigns",
            "Understand search and display advertising concepts",
            "Work with Meta advertising and social media campaigns",
            "Develop social media content strategies",
            "Understand content marketing and audience targeting",
            "Work with email marketing concepts and campaign planning",
            "Use Google Analytics to understand website behaviour",
            "Monitor marketing performance using relevant metrics",
            "Analyse campaign data and identify areas for improvement",
            "Build digital marketing strategies around specific business objectives",
          ],
          closing:
            "The focus isn't on memorising definitions. You'll learn when to use a particular channel, why it matters and how to evaluate its performance.",
        },
        {
          heading: "Work With Digital Marketing Tools",
          paragraphs: [
            "Digital marketing involves working with platforms that help you research, execute and measure campaigns.",
            "During the course, you'll gain practical exposure to tools and platforms used across different areas of digital marketing, including:",
          ],
          bullets: [
            "Google Search Console",
            "Google Analytics",
            "Google Ads",
            "Meta Ads",
            "Keyword research tools",
            "SEO analysis platforms",
            "Content and social media tools",
            "Marketing performance and reporting tools",
          ],
          closing:
            "The tools may change over time, but understanding how to use data to make marketing decisions is the skill that stays relevant.",
        },
        {
          heading: "Build a Marketing Strategy, Not Just Individual Skills",
          paragraphs: [
            "SEO alone doesn't explain everything about digital marketing.",
            "A business might attract visitors through search, engage people through social media, generate leads through advertising and then nurture those leads through email or other channels.",
            "Throughout the course, you'll learn to look at these activities as parts of a larger marketing system.",
            "You'll work on practical exercises and scenarios that help you understand how to:",
            "Research → Plan → Create → Promote → Measure → Optimise",
            "This gives you a more realistic understanding of how digital marketing is handled for businesses rather than learning each channel separately.",
          ],
        },
        {
          heading: "Why Learn Digital Marketing?",
          paragraphs: [
            "Businesses increasingly need people who can understand both the creative and analytical sides of online marketing.",
            "A good digital marketer needs to be able to:",
          ],
          bullets: [
            "Understand an audience",
            "Research what people are searching for",
            "Create useful content",
            "Plan campaigns",
            "Work with advertising platforms",
            "Read performance data",
            "Identify what is working",
            "Improve campaigns based on results",
          ],
          closing:
            "Learning these skills can open opportunities across SEO, paid advertising, social media, content marketing, analytics and broader digital marketing roles. It can also be useful for entrepreneurs, freelancers and business owners who want to understand how their online marketing actually works.",
        },
        {
          heading: "Learn With Live Mentor Support",
          paragraphs: [
            "Digital marketing platforms and algorithms change frequently. When you're learning, it's useful to be able to ask why something works instead of simply following a tutorial.",
            "With live online digital marketing classes, you can discuss campaign ideas, SEO problems, keyword choices, advertising strategies and analytics questions directly with your mentor.",
            "Classes are conducted live through Google Meet, allowing you to learn remotely while still having real-time interaction and guidance.",
          ],
        },
        {
          heading: "Is This Course Right for You?",
          paragraphs: [
            "This Digital Marketing Course Online is suitable if you are:",
          ],
          bullets: [
            "A beginner interested in digital marketing",
            "A student preparing for a marketing or technology career",
            "A graduate looking to develop practical marketing skills",
            "A working professional planning to move into digital marketing",
            "Interested in SEO, Google Ads, social media or content marketing",
            "A freelancer who wants to offer digital marketing services",
            "A business owner who wants to understand online marketing",
            "Someone looking to build practical digital marketing projects",
          ],
          closing:
            "You don't need to be an experienced marketer to begin. The course starts with the fundamentals and progressively introduces the different areas of digital marketing.",
        },
        {
          heading: "Build Skills That Stay With You",
          paragraphs: [
            "Digital marketing isn't about finding one strategy that works forever.",
            "Search behaviour changes. Advertising platforms introduce new features. Algorithms evolve. Audiences respond differently to different types of content.",
            "That's why the more valuable skill is learning how to research, test, measure and improve.",
            "Through live instruction, practical exercises, campaign-based learning and mentor guidance, you'll develop a working understanding of how digital marketing campaigns are planned, executed and evaluated.",
            "The goal is to help you leave the course with more than a certificate—you should have the practical understanding needed to approach a real digital marketing problem and work out what to do next.",
          ],
        },
      ],
      seo: {
        title:
          "Digital Marketing Course Online | Live Training | What Next",
        metaDescription:
          "Learn digital marketing online through live mentor-led classes covering SEO, Google Ads, Meta Ads, content marketing and analytics with What Next.",
      },
    },
  },
  {
    id: "data-analyst",
    code: "DA",
    name: "Data Analyst",
    tagline:
      "Develop practical skills in Excel, SQL, Power BI, Tableau, and data visualization to make business decisions with confidence.",
    level: "Beginner Friendly",
    detail: {
      intro: [
        "Working with data is more than creating spreadsheets or calculating numbers. A good data analyst needs to take raw information, identify what matters, find patterns, and turn those findings into insights that can support better decisions.",
        "Our Data Analyst Course Online is designed to help you develop that process from the ground up. Through live, mentor-led classes, you'll work with data, practise analysis techniques, create visualisations, build reports, and learn how to communicate findings clearly.",
        "Whether you're starting your first analytics role, moving from a non-technical background, or looking to strengthen your existing data skills, the course takes you through the key stages of practical data analysis.",
      ],
      sections: [
        {
          heading: "Learn Data Analysis by Working With Real Data",
          paragraphs: [
            "Learning formulas and memorising commands isn't enough to become a good analyst.",
            "You need to know what to do when you're given an unfamiliar dataset.",
            "Throughout the course, you'll work through practical datasets and exercises where you'll clean information, identify inconsistencies, explore patterns, perform analysis, and present your findings.",
            "You'll learn to approach a dataset with questions such as: What is this data telling me? What is missing? What has changed? Why did it change? How can I present this clearly?",
          ],
          closing:
            "This problem-solving approach helps you develop analytical thinking rather than simply learning a collection of software tools.",
        },
        {
          heading: "What You'll Learn",
          paragraphs: [
            "The curriculum takes you through the core skills needed for practical data analysis. You'll learn how to:",
          ],
          bullets: [
            "Understand data analysis concepts and workflows",
            "Work with Excel for data cleaning and analysis",
            "Use formulas, functions, sorting and filtering effectively",
            "Create pivot tables and meaningful Excel reports",
            "Write SQL queries to retrieve and analyse data",
            "Work with databases and relational data",
            "Clean and transform datasets",
            "Identify missing, duplicate and inconsistent data",
            "Analyse trends, patterns and relationships",
            "Use Python for data analysis",
            "Work with Python libraries such as Pandas and NumPy",
            "Create charts and visualisations from datasets",
            "Build interactive dashboards using Power BI",
            "Present analytical findings through reports and dashboards",
            "Turn business questions into data-driven insights",
          ],
          closing:
            "The learning path moves from working with raw data to presenting insights, giving you an understanding of the complete analysis process.",
        },
        {
          heading: "Work With Practical Datasets",
          paragraphs: [
            "Data analysis becomes easier to understand when you're working with information that resembles the problems analysts encounter in real organisations.",
            "You'll practise working with datasets, cleaning messy information, exploring trends, creating visualisations and building dashboards.",
            'Rather than stopping at "Here\'s the formula," you\'ll focus on "Here\'s the problem. What does the data tell us?"',
          ],
          closing:
            "This helps you understand not only how to perform an analysis, but also how to explain what the result actually means.",
        },
        {
          heading: "Build Reports and Dashboards",
          paragraphs: [
            "Finding an insight is only part of an analyst's job.",
            "The next challenge is communicating it to someone who may not have worked with the data themselves.",
            "You'll learn how to turn your analysis into clear reports and visual dashboards using tools such as Excel and Power BI. You'll work with charts, tables, filters and dashboard elements to present information in a way that makes important patterns easier to understand.",
          ],
          closing:
            "You'll also learn why choosing the right visualisation matters instead of simply filling a dashboard with charts.",
        },
        {
          heading: "Why Learn Data Analytics?",
          paragraphs: [
            "Businesses generate information from sales, customers, operations, marketing, finance and many other activities. The ability to organise that information and extract useful insights can support decision-making across different functions.",
            "Learning data analytics gives you exposure to several complementary skills:",
          ],
          bullets: [
            "Data cleaning",
            "Spreadsheet analysis",
            "SQL",
            "Python",
            "Data visualisation",
            "Dashboard development",
            "Reporting",
            "Analytical thinking",
          ],
          closing:
            "This combination can provide a foundation for roles such as Data Analyst, Business Analyst, Reporting Analyst, BI Analyst and Junior Data Analyst, depending on your experience and the skills you develop further.",
        },
        {
          heading: "Learn With Live Mentor Support",
          paragraphs: [
            "Data analysis often involves questions that aren't answered by a formula or a search result. Why did a query return the wrong result? Why is the dashboard showing unexpected numbers? Which chart should you use? Is the dataset clean enough to analyse?",
            "With live online data analytics classes, you can raise these questions during the session and work through them with your mentor.",
          ],
          closing:
            "Classes are conducted live through Google Meet, giving you direct interaction while allowing you to learn from wherever you are.",
        },
        {
          heading: "Is This Course Right for You?",
          paragraphs: ["This Data Analyst Course Online is suitable if you are:"],
          bullets: [
            "A beginner interested in data and analytics",
            "A student looking to develop practical technical skills",
            "A graduate preparing for an analytics career",
            "A working professional looking to work more effectively with data",
            "Someone moving from a non-technical background into analytics",
            "Interested in Excel, SQL, Python or Power BI",
            "Looking to build practical data analysis projects",
          ],
          closing:
            "You don't need to be an advanced programmer to start. The course can introduce the technical concepts progressively while building your analytical skills along the way.",
        },
        {
          heading: "Build Skills That Stay With You",
          paragraphs: [
            "Tools will change. New analytics platforms will appear. Businesses will continue to work with different types of data.",
            "The more valuable skill is knowing how to approach a data problem, investigate it, find meaningful patterns and communicate what you've discovered.",
            "That's the approach behind the Data Analyst Training at What Next.",
            "Through live instruction, practical datasets, coding and analysis exercises, dashboard building and mentor guidance, you'll develop a workflow that you can continue applying as you work with new datasets and technologies.",
            "The goal isn't simply to teach you Excel, SQL, Python or Power BI individually. It's to help you understand how these skills come together in the work of a data analyst.",
          ],
        },
      ],
      seo: {
        title:
          "Data Analyst Course Online | Live Classes, SQL, Python & Power BI | What Next",
        metaDescription:
          "Learn data analysis online through live mentor-led classes. Build practical skills in Excel, SQL, Python, data visualisation and Power BI through hands-on projects.",
      },
    },
  },
  {
    id: "data-science",
    code: "DS",
    name: "Data Science",
    tagline:
      "Explore statistics, Python, machine learning, data modelling, and real-world predictive analytics projects.",
    level: "Intermediate",
    detail: {
      intro: [
        "Data science brings together statistics, programming and domain knowledge to turn raw information into insights that can guide real decisions.",
        "Our Data Science Course Online is designed around that complete workflow. Through live, mentor-led classes, you'll work with data, apply statistical thinking, build predictive models and learn how to communicate what the data actually means.",
        "Whether you're moving into data science from an analytics background or starting fresh with a strong interest in numbers and programming, the course takes you through the key stages of a real data science project.",
      ],
      sections: [
        {
          heading: "Learn Data Science by Working Through Real Problems",
          paragraphs: [
            "Knowing statistics formulas or machine learning terms isn't the same as knowing how to use them on an actual dataset.",
            "Throughout the course, you'll work through practical exercises where you'll explore data, test hypotheses, build models and evaluate whether those models actually work.",
          ],
          closing:
            "This problem-first approach helps you understand not just how a technique works, but when to use it.",
        },
        {
          heading: "What You'll Learn",
          paragraphs: [
            "The curriculum covers the core skills involved in practical data science. You'll learn how to:",
          ],
          bullets: [
            "Apply statistics and probability to real datasets",
            "Use Python for data analysis and modelling",
            "Work with Pandas, NumPy and visualisation libraries",
            "Clean, transform and prepare data for modelling",
            "Build and evaluate regression and classification models",
            "Understand model accuracy, overfitting and validation",
            "Apply basic machine learning algorithms",
            "Interpret results and communicate findings clearly",
            "Work on end-to-end predictive analytics projects",
          ],
          closing:
            "Each module builds toward being able to take a dataset and produce a working, defensible model from it.",
        },
        {
          heading: "Why Learn Data Science?",
          paragraphs: [
            "Organisations across industries rely on data science to forecast demand, understand customers and automate decisions. Learning data science gives you a foundation that spans statistics, programming and machine learning.",
          ],
        },
        {
          heading: "Learn With Live Mentor Support",
          paragraphs: [
            "Data science involves judgement calls—which model to use, how to handle missing data, how to interpret a result. With live online classes, you can work through those decisions with a mentor instead of guessing alone.",
          ],
        },
        {
          heading: "Is This Course Right for You?",
          paragraphs: ["This Data Science Course Online is suitable if you are:"],
          bullets: [
            "A graduate or professional with basic Python knowledge",
            "A data analyst looking to move into modelling and machine learning",
            "Someone comfortable with fundamental statistics and maths",
            "Interested in predictive analytics and data-driven decision making",
          ],
          closing:
            "Some familiarity with Python and basic statistics will help you get the most out of the course.",
        },
      ],
      seo: {
        title:
          "Data Science Course Online | Live Classes, Statistics & Machine Learning | What Next",
        metaDescription:
          "Learn data science online through live mentor-led classes. Build practical skills in statistics, Python, machine learning and predictive analytics through hands-on projects.",
      },
    },
  },
  {
    id: "ai",
    code: "AI",
    name: "Artificial Intelligence",
    tagline:
      "Understand AI fundamentals, Generative AI, Large Language Models, prompt engineering, and AI-powered application development.",
    level: "Intermediate",
    detail: {
      intro: [
        "Artificial Intelligence is no longer limited to research labs or advanced computer science teams. Today, AI is being used to automate tasks, analyse information, generate content, build intelligent applications and support decisions across different industries.",
        "Our Artificial Intelligence Course Online is designed to help you understand how AI systems are built and applied—not simply how to use an AI tool. Through live, mentor-led classes, you'll gradually move from core concepts into machine learning, working with data, building models and exploring practical AI applications.",
        "Whether you're beginning your journey into AI or already have some programming knowledge and want to move towards intelligent applications, the course gives you a structured path to develop practical AI skills.",
      ],
      sections: [
        {
          heading: "Learn Artificial Intelligence by Building",
          paragraphs: [
            "Understanding AI requires more than knowing a list of algorithms.",
            "You need to understand how data is prepared, how a model learns from that data, how its results are evaluated and what happens when the model doesn't perform as expected.",
            "That's why the learning process focuses on practical implementation.",
            "You'll work through datasets, experiment with models, analyse results and gradually understand how different AI techniques can be applied to real problems.",
            "Instead of treating AI as a collection of complicated concepts, you'll break the process into manageable stages: Understand → Prepare → Train → Evaluate → Improve → Apply",
          ],
          closing:
            "The objective is to help you understand what's happening behind an AI system rather than simply using an existing tool.",
        },
        {
          heading: "What You'll Learn",
          paragraphs: [
            "The curriculum introduces the concepts and practical skills involved in developing AI and machine learning solutions. You'll learn how to:",
          ],
          bullets: [
            "Understand Artificial Intelligence and Machine Learning fundamentals",
            "Work with Python for AI development",
            "Prepare and analyse datasets",
            "Understand data preprocessing techniques",
            "Explore supervised and unsupervised learning",
            "Work with regression and classification problems",
            "Understand clustering and other machine learning approaches",
            "Train and evaluate machine learning models",
            "Identify overfitting and underfitting",
            "Work with relevant Python libraries for AI and machine learning",
            "Understand neural network fundamentals",
            "Explore deep learning concepts",
            "Work with model performance and evaluation techniques",
            "Understand how AI applications process data and produce results",
            "Apply AI concepts through practical projects",
          ],
          closing:
            "The learning path progresses from programming and data fundamentals towards building and evaluating intelligent systems.",
        },
        {
          heading: "Work With Practical AI Projects",
          paragraphs: [
            "AI becomes much easier to understand when you see how the concepts work on an actual problem.",
            "Throughout the course, you'll work through practical exercises and projects where you'll prepare data, select suitable approaches, train models and examine their results.",
            "You'll learn to ask questions such as: Is the data suitable for this problem? Which approach should I use? How accurate is the model? What could be causing poor results?",
            "This encourages you to think about AI as a problem-solving process rather than simply running a machine learning algorithm.",
          ],
          closing:
            "Your projects can also help demonstrate how you've applied the concepts you've learned.",
        },
        {
          heading: "Explore Machine Learning and Modern AI",
          paragraphs: [
            "Artificial intelligence covers a broad range of technologies, and the field continues to evolve.",
            "The course introduces the foundations that help you understand areas such as:",
          ],
          bullets: [
            "Machine Learning",
            "Deep Learning",
            "Natural Language Processing",
            "Computer Vision",
            "Predictive Analytics",
            "Generative AI",
            "AI-powered applications",
          ],
          closing:
            "The emphasis is on understanding the principles behind these technologies so that you can continue exploring more specialised areas as your skills develop.",
        },
        {
          heading: "Why Learn Artificial Intelligence?",
          paragraphs: [
            "AI is being incorporated into software products, business processes, analytics systems, automation workflows and many other technology applications.",
            "Learning AI gives you an opportunity to develop skills across several areas, including:",
          ],
          bullets: [
            "Programming",
            "Data preparation",
            "Machine learning",
            "Model evaluation",
            "Problem-solving",
            "Data-driven decision making",
            "Intelligent application development",
          ],
          closing:
            "This foundation can support further learning in areas such as Machine Learning Engineering, AI Development, Data Science, Generative AI and other specialised AI fields, depending on the direction you choose.",
        },
        {
          heading: "Learn With Live Mentor Support",
          paragraphs: [
            "AI concepts can become difficult when you're trying to understand an algorithm, interpret model output or troubleshoot code without anyone to guide you.",
            "With live online AI classes, you can ask questions during the session, discuss different approaches and understand why a particular model or technique is being used.",
            "Classes are conducted live through Google Meet, allowing you to learn remotely while still having direct interaction with your mentor.",
          ],
          closing:
            "Rather than simply following an AI tutorial, you'll have the opportunity to understand the reasoning behind the implementation.",
        },
        {
          heading: "Is This Course Right for You?",
          paragraphs: [
            "This Artificial Intelligence Course Online is suitable if you are:",
          ],
          bullets: [
            "A beginner interested in Artificial Intelligence",
            "A student exploring AI and machine learning",
            "A graduate preparing for an AI-focused technology career",
            "A programmer looking to move into AI",
            "A working professional interested in developing AI skills",
            "Interested in machine learning, deep learning or Generative AI",
            "Looking to understand how AI models are developed and applied",
          ],
          closing:
            "A basic understanding of programming can be helpful, particularly as you progress into implementation. If you're new to AI, the concepts can be introduced progressively before moving into more advanced topics.",
        },
        {
          heading: "Build Skills That Stay With You",
          paragraphs: [
            "AI tools and frameworks will continue to change. New models, libraries and applications will appear as the technology develops.",
            "That's why learning how to use one particular AI tool isn't enough.",
            "A stronger foundation comes from understanding how AI problems are approached, how data influences a model, how results are evaluated and how an AI solution can be improved.",
            "That's the approach behind the Artificial Intelligence Training at What Next.",
            "Through live instruction, practical coding, datasets, machine learning exercises, AI projects and mentor guidance, you'll build a foundation that allows you to continue exploring the rapidly developing field of artificial intelligence.",
            "The goal isn't simply to teach you how to run an AI model. It's to help you understand how intelligent systems are developed, tested and applied to real problems.",
          ],
        },
      ],
      seo: {
        title: "Artificial Intelligence Course Online | Live AI & ML Classes | What Next",
        metaDescription:
          "Learn Artificial Intelligence online through live mentor-led classes. Explore Python, machine learning, deep learning, Generative AI and practical AI projects with What Next.",
      },
    },
  },
  {
    id: "ui-ux",
    code: "UX",
    name: "UI / UX Design",
    tagline:
      "Design intuitive digital experiences using Figma, wireframing, user research, prototyping, and usability testing.",
    level: "Beginner Friendly",
    detail: {
      intro: [
        "A good digital product isn't defined only by how it looks. It needs to be easy to understand, simple to navigate, and designed around what people actually need to accomplish.",
        "Our UI/UX Design Course Online is designed to help you understand both sides of digital product design—User Experience (UX) and User Interface (UI). Through live, mentor-led classes, you'll learn how to understand users, structure information, create wireframes, design interfaces, build prototypes, and refine your ideas based on feedback.",
        "Whether you're starting from scratch, moving into design from another field, or looking to strengthen your existing design skills, the course takes you through the design process step by step.",
      ],
      sections: [
        {
          heading: "Learn UI/UX by Designing, Not Just Watching",
          paragraphs: [
            "Knowing how to use a design tool doesn't automatically make someone a UI/UX designer.",
            "The important part is understanding why a screen should be designed in a particular way.",
            "Throughout the course, you'll work through the stages of a design project—from understanding a user's needs to planning the experience, creating wireframes, designing interfaces and developing interactive prototypes.",
            "You'll learn to question design decisions: Who is using this product? What are they trying to do? Where might they get confused? How can the interface make that task easier?",
          ],
          closing:
            "This approach helps you move beyond simply creating attractive screens and start thinking about the experience behind them.",
        },
        {
          heading: "What You'll Learn",
          paragraphs: [
            "The curriculum introduces the key skills involved in modern UI/UX design. You'll learn how to:",
          ],
          bullets: [
            "Understand UI and UX design fundamentals",
            "Research users and identify their needs",
            "Define user problems and design objectives",
            "Create user personas and user journeys",
            "Organise content through information architecture",
            "Develop user flows",
            "Create low-fidelity wireframes",
            "Design high-fidelity user interfaces",
            "Understand layout, spacing, typography and visual hierarchy",
            "Work with colour and design systems",
            "Create responsive designs for different screen sizes",
            "Build interactive prototypes",
            "Conduct usability testing and gather feedback",
            "Identify usability problems and improve designs",
            "Create reusable UI components",
            "Prepare designs for developer handoff",
            "Build projects that demonstrate your design process",
          ],
          closing:
            "The learning path takes you from understanding the problem to presenting a complete design solution.",
        },
        {
          heading: "Design Complete Digital Experiences",
          paragraphs: [
            "A UI/UX project doesn't begin with opening a design tool.",
            "It begins with understanding the problem.",
            "You'll work through practical design exercises where you'll explore a user's needs, map their journey, plan the structure of a product, create wireframes and turn those ideas into polished interfaces.",
            "You'll also practise presenting your design decisions and making improvements based on feedback.",
          ],
          closing:
            "The objective is to help you understand the complete process behind a digital product—not simply produce a collection of visually attractive screens.",
        },
        {
          heading: "Work With UI/UX Design Tools",
          paragraphs: [
            "Designers need tools to turn their ideas into something that can be tested, discussed and developed.",
            "During the course, you'll gain practical experience with commonly used design workflows and tools such as Figma, depending on the curriculum and projects. You'll use design tools to:",
          ],
          bullets: [
            "Create wireframes",
            "Develop UI layouts",
            "Build reusable components",
            "Create prototypes",
            "Test user flows",
            "Organise design systems",
            "Present design concepts",
            "Prepare screens for developer collaboration",
          ],
          closing:
            "The focus remains on design thinking and problem-solving, rather than simply learning where each button in a software tool is located.",
        },
        {
          heading: "Build a Portfolio Through Practical Projects",
          paragraphs: [
            "For a designer, showing your process can be just as important as showing the final screen.",
            "Throughout the course, you'll work on practical projects that allow you to demonstrate how you moved from a problem or requirement to a finished design. Your project work can include:",
            "Research → User Flow → Wireframe → UI Design → Prototype → Feedback → Refinement",
          ],
          closing:
            "This gives you material that can contribute towards a UI/UX design portfolio and helps you explain the reasoning behind your design decisions during interviews or client discussions.",
        },
        {
          heading: "Why Learn UI/UX Design?",
          paragraphs: [
            "Almost every digital product depends on people being able to understand and use it effectively. UI/UX design brings together several skills, including:",
          ],
          bullets: [
            "User research",
            "Problem-solving",
            "Visual communication",
            "Interaction design",
            "Information architecture",
            "Prototyping",
            "Usability testing",
            "Design systems",
          ],
          closing:
            "Developing these skills can prepare you for different design-focused roles such as UI Designer, UX Designer, Product Designer, UX/UI Designer or Interaction Designer, depending on the direction you choose to pursue.",
        },
        {
          heading: "Learn With Live Mentor Support",
          paragraphs: [
            "Design can be subjective, but good design decisions should have a reason behind them.",
            "With live online UI/UX classes, you can present your work, discuss your design choices, receive feedback and understand how your design can be improved.",
            "Instead of completing a project and wondering whether your approach is right, you can interact with your mentor during the learning process.",
            "Classes are conducted live through Google Meet, allowing you to learn remotely while still receiving direct guidance and feedback.",
          ],
        },
        {
          heading: "Is This Course Right for You?",
          paragraphs: ["This UI/UX Design Course Online is suitable if you are:"],
          bullets: [
            "A beginner interested in digital design",
            "A student exploring UI/UX as a career",
            "A graduate looking to develop creative and technical design skills",
            "A graphic designer interested in moving into digital product design",
            "A working professional planning a career change",
            "Interested in website and mobile app design",
            "Looking to build a professional UI/UX portfolio",
            "Someone who enjoys solving user problems through design",
          ],
          closing:
            "You don't need to be an experienced designer to begin. The course introduces the design process progressively and gives you opportunities to practise each stage.",
        },
        {
          heading: "Build Skills That Stay With You",
          paragraphs: [
            "Design tools will change. Interfaces will evolve. New platforms and design trends will appear.",
            "The more valuable skill is knowing how to understand a user, identify a problem, structure an experience and make thoughtful design decisions.",
            "That's the approach behind the UI/UX Design Training at What Next.",
            "Through live instruction, practical design exercises, portfolio projects, mentor feedback and hands-on prototyping, you'll develop a process that you can continue using across different websites, applications and digital products.",
            "The goal isn't simply to teach you how to create attractive screens. It's to help you become a designer who can understand the problem behind the screen and design an experience that makes sense to the person using it.",
          ],
        },
      ],
      seo: {
        title:
          "UI/UX Design Course Online | Live Classes & Portfolio Projects | What Next",
        metaDescription:
          "Learn UI/UX design online through live mentor-led classes covering research, wireframing, Figma prototyping and usability testing with What Next.",
      },
    },
  },
  {
    id: "graphic-design",
    code: "GD",
    name: "Graphic Design",
    tagline:
      "Create professional visual content using Photoshop, Illustrator, branding principles, and modern design techniques.",
    level: "Beginner Friendly",
    detail: {
      intro: [
        "Graphic design is not simply about making something look attractive. A strong design needs to communicate an idea, guide attention, create the right impression, and work for the audience it is intended for.",
        "Our Graphic Design Course Online is designed to help you develop that thinking alongside the practical skills needed to create professional visual content. Through live, mentor-led classes, you'll learn design fundamentals, work with industry-relevant design tools, explore different types of visual communication, and develop projects that allow you to put your skills into practice.",
        "Whether you're starting from scratch, exploring design as a career, or looking to turn your existing creative interest into a practical skill, the course gives you a structured path from design fundamentals to portfolio development.",
      ],
      sections: [
        {
          heading: "Learn Graphic Design by Creating",
          paragraphs: [
            "Knowing how to use Photoshop or Illustrator is only one part of graphic design.",
            "The more important question is: Why does this design work?",
            "Throughout the course, you'll learn how typography, colour, composition, imagery, spacing and visual hierarchy influence the way people understand a design.",
            "You'll work on practical design tasks where you'll need to consider the purpose of the design, the target audience and the message before deciding how the final artwork should look.",
            "Instead of simply copying design styles, you'll learn to make deliberate design choices and understand the reasoning behind them.",
          ],
          closing:
            'The objective is to help you move from "I can use a design tool" to "I can create a design that communicates something clearly."',
        },
        {
          heading: "What You'll Learn",
          paragraphs: [
            "The curriculum introduces the fundamental principles and practical skills involved in graphic design. You'll learn how to:",
          ],
          bullets: [
            "Understand graphic design principles and visual communication",
            "Work with typography and font pairing",
            "Apply colour theory and colour combinations",
            "Understand composition, alignment, balance and spacing",
            "Use grids and visual hierarchy effectively",
            "Work with images, shapes and graphic elements",
            "Create logos and basic brand identity assets",
            "Design social media creatives",
            "Create posters, banners and promotional materials",
            "Design marketing and advertising creatives",
            "Understand image editing and manipulation",
            "Create vector-based artwork",
            "Prepare designs for digital and print use",
            "Work with professional graphic design software",
            "Present and organise your design work",
            "Build practical projects for your portfolio",
          ],
          closing:
            "The learning process moves from design fundamentals to creating finished visual communication pieces, helping you understand both the creative and technical sides of graphic design.",
        },
        {
          heading: "Work on Practical Design Projects",
          paragraphs: [
            "Graphic design becomes easier to understand when you're creating something with a specific purpose.",
            "During the course, you'll work on different types of design projects rather than completing only isolated tool exercises. You may work on designs such as:",
          ],
          bullets: [
            "Social media posts",
            "Posters",
            "Digital advertisements",
            "Promotional banners",
            "Brand identity concepts",
            "Marketing creatives",
            "Presentation graphics",
            "Other visual communication projects",
          ],
          closing:
            "Each project gives you an opportunity to think about the brief, audience, message and visual direction before developing the final design, helping you build a body of work that can eventually contribute to your graphic design portfolio.",
        },
        {
          heading: "Work With Graphic Design Tools",
          paragraphs: [
            "Professional design requires more than creativity. You also need to be comfortable turning an idea into a finished digital asset.",
            "Depending on the course curriculum, you'll gain practical experience with commonly used tools such as Adobe Photoshop, Adobe Illustrator and other relevant design software. You'll learn how to use design software for tasks such as:",
          ],
          bullets: [
            "Image editing and manipulation",
            "Creating vector graphics",
            "Logo and branding work",
            "Typography-based designs",
            "Social media creatives",
            "Posters and promotional materials",
            "Digital advertising designs",
            "Preparing artwork for different formats",
          ],
          closing:
            "The emphasis isn't simply on learning where each tool is located. You'll learn how to use the tools to solve actual design requirements.",
        },
        {
          heading: "Build a Graphic Design Portfolio",
          paragraphs: [
            "For a designer, a portfolio can communicate your abilities more effectively than a list of software you know.",
            "That's why practical project work is an important part of the learning process.",
            "You'll develop designs that can demonstrate different aspects of your skills—from typography and composition to branding, image editing and promotional design. Your project process can follow a simple structure:",
            "Brief → Research → Concept → Design → Feedback → Refinement → Final Artwork",
          ],
          closing:
            "This helps you understand that professional design is often an iterative process rather than creating the perfect design on the first attempt.",
        },
        {
          heading: "Why Learn Graphic Design?",
          paragraphs: [
            "Graphic design is used wherever ideas need to be communicated visually.",
            "Businesses need designers for branding, advertising, social media, marketing materials, websites, presentations, publications and many other forms of communication. Learning graphic design can help you develop skills in:",
          ],
          bullets: [
            "Visual communication",
            "Creative problem-solving",
            "Branding",
            "Typography",
            "Layout",
            "Image editing",
            "Digital content creation",
            "Marketing design",
          ],
          closing:
            "Depending on your interests and experience, these skills can support career paths such as Graphic Designer, Visual Designer, Brand Designer, Social Media Designer, Marketing Designer or Creative Designer.",
        },
        {
          heading: "Learn With Live Mentor Support",
          paragraphs: [
            "Design is easier to improve when someone can look at your work and explain why something could be changed.",
            "With live online graphic design classes, you can share your work, discuss design choices, receive feedback and make improvements while you're learning.",
            "Instead of completing a design independently and wondering whether it works, you'll have the opportunity to understand how your mentor evaluates elements such as composition, typography, colour, spacing and visual consistency.",
            "Classes are conducted live through Google Meet, allowing you to learn from wherever you are while still receiving direct interaction and guidance.",
          ],
        },
        {
          heading: "Is This Course Right for You?",
          paragraphs: ["This Graphic Design Course Online is suitable if you are:"],
          bullets: [
            "A beginner interested in graphic design",
            "A student exploring creative career options",
            "A graduate looking to develop a practical design skill",
            "A social media professional who wants to create better visual content",
            "A marketing professional interested in design",
            "A freelancer planning to offer design services",
            "Someone interested in branding and visual communication",
            "Looking to develop a professional graphic design portfolio",
          ],
          closing:
            "You don't need to be an experienced designer to begin. The course starts with fundamental design principles and gradually introduces more advanced creative and technical skills.",
        },
        {
          heading: "Build Skills That Stay With You",
          paragraphs: [
            "Design trends change quickly. New styles appear, platforms evolve and software continues to introduce new features.",
            "But the fundamentals of good visual communication remain useful.",
            "Understanding how to use typography, colour, composition, hierarchy and imagery to communicate an idea gives you a foundation that can be applied across different projects and design tools.",
            "That's the approach behind the Graphic Design Training at What Next.",
            "Through live instruction, practical design assignments, software training, portfolio projects and mentor feedback, you'll develop the ability to approach a design brief, turn an idea into a visual concept and refine it into a finished piece.",
            "The goal isn't simply to teach you how to operate design software. It's to help you develop the creative thinking and practical design skills needed to create purposeful visual communication.",
          ],
        },
      ],
      seo: {
        title:
          "Graphic Design Course Online | Live Classes & Portfolio Projects | What Next",
        metaDescription:
          "Learn graphic design online through live mentor-led classes covering Photoshop, Illustrator, branding and visual communication with What Next.",
      },
    },
  },
  {
    id: "video-editing",
    code: "VE",
    name: "Video Editing",
    tagline:
      "Learn professional video editing, motion graphics, colour correction, transitions, storytelling, and content creation workflows.",
    level: "Beginner Friendly",
    detail: {
      intro: [
        "Video editing is more than putting clips together and adding transitions. A good editor knows how to take raw footage, understand the purpose of the video, control its pace, shape the story, and turn separate clips into something people want to watch.",
        "Our Video Editing Course Online is designed to help you develop that complete editing process through live, mentor-led classes. You'll learn how to work with raw footage, build a sequence, improve visual and audio quality, add motion and effects, and prepare videos for different platforms.",
        "Whether you're starting from zero, interested in content creation, or looking to develop video editing as a professional skill, the course gives you a practical path from basic editing to creating polished video projects.",
      ],
      sections: [
        {
          heading: "Learn Video Editing by Creating",
          paragraphs: [
            "Knowing where the cut, transition or effect button is doesn't make someone a good editor.",
            "The real skill is knowing when to cut, what to remove, how to maintain attention, and how to make the story flow naturally.",
            "Throughout the course, you'll work with raw footage and learn how to turn it into a finished video. You'll practise trimming clips, arranging sequences, working with audio, adding titles and effects, correcting visual issues, and refining the final output.",
            "You'll also learn to think about the purpose of a video before editing it. Is it a short social media video? A YouTube video? A promotional video? A presentation? A storytelling piece? The editing approach can change depending on the answer.",
          ],
          closing:
            'The objective is to help you move from "I know how to edit a video" to "I know how to edit a video for a specific audience and purpose."',
        },
        {
          heading: "What You'll Learn",
          paragraphs: [
            "The curriculum takes you through the core skills involved in modern video editing. You'll learn how to:",
          ],
          bullets: [
            "Understand video editing fundamentals and workflows",
            "Organise and manage raw footage",
            "Import and arrange media files",
            "Trim, cut and combine video clips",
            "Build sequences and maintain continuity",
            "Control pacing and storytelling",
            "Work with transitions appropriately",
            "Add titles, captions and graphics",
            "Edit and synchronise audio",
            "Adjust music, dialogue and sound effects",
            "Apply colour correction and basic colour grading",
            "Use visual effects where appropriate",
            "Create motion-based elements and animations",
            "Work with different video formats and resolutions",
            "Edit videos for YouTube and social media platforms",
            "Export videos using suitable settings",
            "Organise projects for efficient editing",
            "Complete practical video editing projects",
          ],
          closing:
            "The learning path moves from raw footage to a finished video, helping you understand what happens at each stage of the editing workflow.",
        },
        {
          heading: "Work With Real Editing Projects",
          paragraphs: [
            "Video editing becomes much easier to understand when you're working with footage rather than simply following software tutorials.",
            "During the course, you'll work on practical projects where you'll take raw media and turn it into a structured final video. You may work with different formats such as:",
          ],
          bullets: [
            "YouTube videos",
            "Short-form social media videos",
            "Promotional videos",
            "Reels and vertical content",
            "Product or business videos",
            "Storytelling and presentation videos",
          ],
          closing:
            "Each project gives you an opportunity to practise the complete workflow: Import → Organise → Cut → Arrange → Enhance → Add Audio → Refine → Export, giving you practical work that can also contribute towards a video editing portfolio.",
        },
        {
          heading: "Work With Video Editing Tools",
          paragraphs: [
            "A professional editing workflow requires both creative judgement and technical control.",
            "Depending on the course curriculum, you'll gain hands-on experience with relevant video editing software such as Adobe Premiere Pro, DaVinci Resolve or other editing tools. You'll learn how to use editing software for:",
          ],
          bullets: [
            "Timeline-based editing",
            "Cutting and sequencing",
            "Audio editing",
            "Titles and captions",
            "Transitions",
            "Colour correction",
            "Effects",
            "Motion elements",
            "Video formatting",
            "Export and delivery",
          ],
          closing:
            "The focus isn't simply on learning software features. You'll learn when and why to use them while working on actual editing tasks.",
        },
        {
          heading: "Learn to Edit for Different Platforms",
          paragraphs: [
            "A video isn't always created in the same format.",
            "A long-form YouTube video has different requirements from a short vertical Reel. A promotional video may need a different pace from an educational video. You'll learn how factors such as:",
          ],
          bullets: [
            "Video duration",
            "Aspect ratio",
            "Audience",
            "Platform",
            "Story structure",
            "Audio",
            "Visual pacing",
          ],
          closing:
            "can influence editing decisions, helping you create videos that are designed for their intended viewing experience rather than simply exporting the same video everywhere.",
        },
        {
          heading: "Build a Video Editing Portfolio",
          paragraphs: [
            "For an editor, the final video often says more than a list of software skills.",
            "That's why practical projects are an important part of the learning process.",
            "You'll have opportunities to create different types of edited content that demonstrate your ability to work with footage, structure a sequence, manage audio, improve visuals and deliver a finished video. Your editing process can follow:",
            "Brief → Footage → Story Structure → Rough Cut → Fine Cut → Audio & Visuals → Review → Final Export",
          ],
          closing:
            "This gives you a clearer understanding of how an editing project develops from raw material into a finished piece, and your completed projects can also form the foundation of a video editing portfolio.",
        },
        {
          heading: "Why Learn Video Editing?",
          paragraphs: [
            "Video has become an important format for communicating ideas online, from educational content and entertainment to advertising, social media and business communication. Learning video editing can help you develop skills across:",
          ],
          bullets: [
            "Storytelling",
            "Visual communication",
            "Content creation",
            "Audio editing",
            "Motion and visual effects",
            "Social media content",
            "Promotional video production",
            "Post-production",
          ],
          closing:
            "Depending on your interests and experience, these skills can support roles such as Video Editor, Video Content Editor, Social Media Video Editor, YouTube Video Editor, Post-Production Editor or Content Creator.",
        },
        {
          heading: "Learn With Live Mentor Support",
          paragraphs: [
            "Editing can involve hundreds of small decisions. Sometimes you may know what you want the video to look like but not know how to achieve it.",
            "With live online video editing classes, you can work through those challenges with your mentor during the learning process.",
            "You can ask questions about editing techniques, show your work, understand why a particular cut or effect isn't working, and receive feedback on how the final video can be improved.",
            "Classes are conducted live through Google Meet, allowing you to learn remotely while still receiving direct interaction and guidance.",
          ],
        },
        {
          heading: "Is This Course Right for You?",
          paragraphs: ["This Video Editing Course Online is suitable if you are:"],
          bullets: [
            "A beginner interested in video editing",
            "A student exploring creative career options",
            "A graduate looking to develop a practical creative skill",
            "Interested in YouTube or social media content",
            "A content creator wanting to improve video quality",
            "A digital marketer who wants to create better video content",
            "A freelancer planning to offer video editing services",
            "Someone looking to build a professional video editing portfolio",
          ],
          closing:
            "You don't need professional editing experience to begin. The course starts with the fundamentals and progressively introduces more advanced editing techniques.",
        },
        {
          heading: "Build Skills That Stay With You",
          paragraphs: [
            "Editing software will continue to change. New effects, AI-powered features and editing tools will keep appearing.",
            "But the fundamentals of good editing remain useful.",
            "Understanding storytelling, pacing, composition, sound, continuity and audience attention gives you skills that can be applied even when the software or platform changes.",
            "That's the approach behind the Video Editing Training at What Next.",
            "Through live instruction, practical footage, editing assignments, mentor feedback and project-based learning, you'll develop a workflow that can be applied to different types of video content.",
            "The goal isn't simply to teach you how to operate an editing program. It's to help you become an editor who can take raw footage, understand the purpose behind it and shape it into a video that communicates effectively.",
          ],
        },
      ],
      seo: {
        title: "Video Editing Course Online | Live Classes & Practical Projects | What Next",
        metaDescription:
          "Learn video editing online through live mentor-led classes. Learn editing, audio, colour correction, effects and storytelling through practical video projects with What Next.",
      },
    },
  },
];

// ============================================
// FAQ DATA (from provided site copy)
// ============================================
export const FAQS = [
  {
    q: "Are the classes live or recorded?",
    a: "Every course is delivered through live online classes on Google Meet, allowing students to interact with mentors, ask questions, and receive real-time guidance.",
  },
  {
    q: "Who can join these courses?",
    a: "Students, fresh graduates, working professionals, freelancers, and career changers can join our programs based on their interests and career goals.",
  },
  {
    q: "Will I work on real projects?",
    a: "Yes. Every learning track includes practical assignments and industry-relevant projects to help you build a portfolio.",
  },
  {
    q: "Which online course is best for getting a job?",
    a: "The right course depends on your career goals. Popular choices include Full Stack Development, Python, Data Analytics, Artificial Intelligence, and Digital Marketing because of their strong industry demand.",
  },
  {
    q: "How do I enroll?",
    a: "Simply contact us through WhatsApp or call our team. We'll help you choose a suitable batch and guide you through the enrollment process.",
  },
];